// Ephemeral on-device storage for the Moments social feed.
//
// Two buckets:
//   - self   : last 10 screenshots the user posted
//   - friend : last 10 across all friends combined
//
// Nothing persists server-side, nothing syncs between devices, and
// each bucket is hard-capped to 10 items with the oldest evicted on
// insert. That cap is what makes Moments feel like a feed instead of
// a photo library. Users should never accumulate hundreds of
// screenshots here.
//
// Blobs go in IndexedDB (not localStorage) so WebP image data is
// stored as binary instead of base64-inflated strings. Typical
// bucket = 10 × ~50 KB = 500 KB. Both buckets combined peak around
// 1.5 MB per installed client, which is negligible on disk and well
// under any IndexedDB quota.

const DB_NAME = 'valkenhall-moments';
const DB_VERSION = 1;
const STORE = 'moments';
const BUCKET_CAP = 10;

function openDb() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        const store = db.createObjectStore(STORE, { keyPath: 'id' });
        store.createIndex('bucket_createdAt', ['bucket', 'createdAt']);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function promisifyRequest(req) {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function withStore(mode, fn) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, mode);
    const store = tx.objectStore(STORE);
    const result = fn(store);
    tx.oncomplete = () => {
      db.close();
      Promise.resolve(result).then(resolve, reject);
    };
    tx.onerror = () => {
      db.close();
      reject(tx.error);
    };
    tx.onabort = () => {
      db.close();
      reject(tx.error);
    };
  });
}

// List everything in a bucket, newest first. Returns { id, bucket,
// authorId, authorName, authorAvatar, createdAt, width, height, blob }.
export async function listBucket(bucket) {
  return withStore('readonly', (store) => {
    const index = store.index('bucket_createdAt');
    const range = IDBKeyRange.bound([bucket, -Infinity], [bucket, Infinity]);
    return new Promise((resolve, reject) => {
      const out = [];
      const req = index.openCursor(range, 'prev'); // prev = descending createdAt
      req.onsuccess = () => {
        const cur = req.result;
        if (!cur) return resolve(out);
        out.push(cur.value);
        cur.continue();
      };
      req.onerror = () => reject(req.error);
    });
  });
}

// Insert into a bucket and evict overflow in the same transaction. The
// eviction query runs the same index cursor we read with so there's no
// "insert then separate prune" race: at commit time the bucket has
// BUCKET_CAP items or fewer, always.
export async function insertIntoBucket(bucket, entry) {
  const record = { ...entry, bucket };
  return withStore('readwrite', (store) => {
    store.put(record);
    const index = store.index('bucket_createdAt');
    const range = IDBKeyRange.bound([bucket, -Infinity], [bucket, Infinity]);
    return new Promise((resolve, reject) => {
      const req = index.openCursor(range, 'prev');
      let count = 0;
      req.onsuccess = () => {
        const cur = req.result;
        if (!cur) return resolve(record);
        count += 1;
        if (count > BUCKET_CAP) cur.delete();
        cur.continue();
      };
      req.onerror = () => reject(req.error);
    });
  });
}

export async function deleteMoment(id) {
  return withStore('readwrite', (store) => promisifyRequest(store.delete(id)));
}

// Clear a bucket on logout so someone else signing in on this device
// doesn't inherit the previous user's feed.
export async function clearAll() {
  return withStore('readwrite', (store) => promisifyRequest(store.clear()));
}

export const MOMENTS_CAP = BUCKET_CAP;
